import m, {MithrilComponent, type Children} from '@bitstillery/mithril'

import type {CmsComponentMeta} from '@bitstillery/common/cms/catalog_meta'
import {html_element} from '@bitstillery/common/lib/dom'

export const marqueeStripMeta = {
    tier: 'data',
    summary: 'Horizontal strip that scrolls its `items` and bounces back at the ends (stays put when they already fit).',
    when_to_use:
        'Feed it `items` — arbitrary content, e.g. brand names mapped from an `<AsyncData source="portal/brands">`. Drop it into a `HeroBand` `footer` or anywhere a scrolling strip fits.',
    example: `
    <MarqueeStrip items={['Whisky', 'Rum', 'Gin', 'Vodka', 'Cognac']} />
    `,
} satisfies CmsComponentMeta

export interface MarqueeStripAttrs {
    /** The tiles to scroll — arbitrary content (text or vnodes); the strip scrolls them and bounces back. */
    items: Children[]
}

/** Pixels per second the row travels — keeps the bounce a steady speed regardless of how far it overflows. */
const SCROLL_SPEED = 80

/**
 * A horizontal marquee: renders `items` as a single row and, once mounted, measures how far the row
 * overflows the strip and drives a bounce (scroll to the far end, then reverse) via CSS custom properties.
 * When the items already fit, the shift is `0` so it simply stays put. Styles: co-located `marquee_strip.css`.
 */
export class MarqueeStrip extends MithrilComponent<MarqueeStripAttrs> {
    private root: HTMLElement | null = null
    private track: HTMLElement | null = null
    private observer: ResizeObserver | undefined

    override oncreate(vnode: m.VnodeDOM<MarqueeStripAttrs>) {
        if (globalThis.__SSR_MODE__) {
            return
        }
        this.root = html_element(vnode.dom)
        this.track = this.root?.querySelector<HTMLElement>('.track') ?? null
        this.measure()
        // Re-measure on viewport/zoom changes (root) and content/font reflow (track).
        if (typeof ResizeObserver !== 'undefined' && this.root && this.track) {
            this.observer = new ResizeObserver(() => this.measure())
            this.observer.observe(this.root)
            this.observer.observe(this.track)
        }
    }

    override onremove() {
        this.observer?.disconnect()
    }

    /** Set the travel distance (and a matching duration for constant speed); `0` when nothing overflows. */
    private measure() {
        if (!this.root || !this.track) {
            return
        }
        const overflow = Math.max(0, this.track.scrollWidth - this.root.clientWidth)
        this.root.style.setProperty('--marquee-shift', `${-overflow}px`)
        this.root.style.setProperty('--marquee-duration', `${Math.max(5, overflow / SCROLL_SPEED)}s`)
    }

    view(vnode: m.Vnode<MarqueeStripAttrs>) {
        const {items} = vnode.attrs
        return (
            <section className='c-marquee-strip'>
                <div className='track'>
                    {items.map((item, i) => (
                        <div className='item' key={i}>
                            {item}
                        </div>
                    ))}
                </div>
            </section>
        )
    }
}
